/**
 * Remember that this visitor already gave their email to view a certificate, so they are
 * not re-asked on every scan.
 *
 * ## What was broken
 *
 * `CertificatePage` initialised `isAccessDataCollected` to a bare `useState(false)`, so the
 * gate reappeared on EVERY mount — a refresh of the same certificate in the same tab
 * re-asked. There was a `sessionStorage.setItem('certificate_access:<id>', …)` in the
 * since-deleted `pages/CertificateAccess`, but nothing ever read it back and nothing
 * navigated to that route, so it never took effect. The intent was there; the read half
 * was never written.
 *
 * ## Scope: per ORIGIN, not per certificate
 *
 * One record covers every certificate on this origin. localStorage is already partitioned
 * by origin, and an origin is one brand's domain — so this can never leak a decision from
 * one brand to another, which keeps it safe whether Vera or the brand is the data
 * controller. Cross-brand memory is deliberately NOT possible here: the app mounts inside
 * the brand's page, third-party cookies are blocked in Safari/Firefox and partitioned in
 * Chrome, so the only mechanism that could span brands is a server-side record keyed to an
 * identity. That is an account feature, tracked separately.
 *
 * ## What is cached, and what is deliberately not
 *
 * The EMAIL is cached — it is stable identity and re-typing it is the friction being
 * removed. The LOCATION VALUE is not. Replaying a months-old position on a new scan would
 * log a stale location as though it were where the scan happened, which is data
 * corruption, not convenience. Only the location CONSENT flag is kept; the position is
 * re-acquired per access (silently, since the browser's own permission grant persists
 * per-origin and no prompt is needed once granted).
 *
 * ## TTL
 *
 * 180 days, absolute from the moment consent was given — not sliding. Sliding would mean
 * an active visitor is never re-asked, which defeats having a refresh interval at all.
 * Six months is CNIL's recommended consent-refresh interval and sits inside ICO's 6–24
 * month guidance, so the number is defensible rather than arbitrary. It is also long
 * enough to be useful: a certificate is a low-frequency object, and a 30-day TTL would
 * expire before the realistic second scan, making the cache pointless.
 *
 * This is a CONVENIENCE CACHE, not the consent record. The authoritative record is the
 * server-side access log, which is written per access with its own timestamp — including
 * on a cached hit, so suppressing the prompt never suppresses the brand's analytics.
 *
 * ## VRG-591: the cache must be re-checked against the CURRENT requirement
 *
 * `collect_location` is now a HARD GATE, not a best-effort extra (see
 * `requestPositionForAccess`). A record cached while location was optional — or while
 * the brand collected none — asserts nothing about location, and a record cached under
 * a grant the visitor has since revoked in their browser asserts something that is no
 * longer true. Either would wave someone past a gate they no longer satisfy, for up to
 * 180 days. `cachedAccessSatisfies` is the one predicate that decides it, and BOTH
 * consumers (the gate route and the certificate page) call it, so the two can never
 * disagree and bounce a visitor between them.
 */

const KEY = 'vera:certificate-access';

/** 180 days in ms. See the TTL note above. */
export const ACCESS_CONSENT_TTL_MS = 180 * 24 * 60 * 60 * 1000;

export interface AccessConsent {
  /**
   * OPTIONAL, because a brand may require a LOCATION and no email. That gate has no
   * email field on it, so the consent it writes asserts a location and nothing else.
   *
   * Absent is not "unknown" — it is a positive statement that no email was given, and
   * `cachedAccessSatisfies` reads it back as failing an email requirement rather than
   * as satisfying one by default.
   */
  email?: string;
  /** Whether they agreed to share location — NOT a cached position. */
  locationConsent: boolean;
  /** Epoch ms the consent was given. Absolute expiry is measured from here. */
  grantedAt: number;
}

export const readAccessConsent = (): AccessConsent | null => {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<AccessConsent>;
    if (typeof parsed?.grantedAt !== 'number') return null;

    // A record has to ASSERT something. This used to be `!parsed.email`, which was a
    // sound integrity check while every gate collected an email — and which would now
    // silently discard every location-only consent, re-gating those visitors on each
    // scan for the life of the setting. So the requirement widens to "at least one of
    // the two halves is present" rather than dropping: `{}` with a valid timestamp is
    // still a corrupt entry and still rejected, because it would read as a consent
    // that satisfies whichever requirement happened not to be asked for.
    const email = typeof parsed.email === 'string' && parsed.email ? parsed.email : undefined;
    const locationConsent = Boolean(parsed.locationConsent);
    if (!email && !locationConsent) return null;

    if (Date.now() - parsed.grantedAt > ACCESS_CONSENT_TTL_MS) {
      window.localStorage.removeItem(KEY);
      return null;
    }

    return { email, locationConsent, grantedAt: parsed.grantedAt };
  } catch {
    // Private mode, disabled storage, or a corrupt entry. Falling back to null just means
    // the visitor is asked again — never a hard failure of the page.
    return null;
  }
};

/**
 * `email` is `undefined` on the location-only gate, which has no email field to collect
 * one from. Writing `''` instead would round-trip through `readAccessConsent` as a
 * falsy-but-present address and make "no email was given" indistinguishable from
 * "an empty one was", so the key is omitted from the stored JSON entirely.
 */
export const writeAccessConsent = (
  email: string | undefined,
  locationConsent: boolean,
): void => {
  try {
    window.localStorage.setItem(
      KEY,
      JSON.stringify({
        ...(email ? { email } : {}),
        locationConsent,
        grantedAt: Date.now(),
      } satisfies AccessConsent),
    );
  } catch {
    // Storage unavailable — the access still succeeded, they will just be asked next time.
  }
};

export const clearAccessConsent = (): void => {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* nothing to do */
  }
};

export type GeolocationPermissionState = 'granted' | 'denied' | 'prompt' | 'unknown';

/**
 * What the browser's permission store says BEFORE we ask for a position.
 *
 * Recovered from the since-deleted `pages/CertificateAccess`. It exists so the `denied` case can
 * be answered with the right instruction deterministically, rather than inferred from an
 * error code after a request that was never going to succeed.
 *
 * `unknown` is not a failure — `navigator.permissions` is absent on Safari before 16, and
 * on those browsers the position request itself still works. Every caller here treats
 * `unknown` as "carry on", never as "denied".
 */
export const getGeolocationPermissionState =
  async (): Promise<GeolocationPermissionState> => {
    try {
      if (!navigator?.permissions?.query) return 'unknown';
      const status = await navigator.permissions.query({
        name: 'geolocation' as PermissionName,
      });
      return (status?.state as GeolocationPermissionState) || 'unknown';
    } catch {
      return 'unknown';
    }
  };

/**
 * A position attempt either produced coordinates or failed for a reason the visitor can
 * act on. THE REASON IS CARRIED rather than collapsed to `null`, because the failures
 * need opposite instructions and telling the wrong one costs the visitor the page.
 *
 * `blocked` is the one reason the visitor CANNOT act on, and it exists so we stop
 * telling them to fix something they have no access to — see
 * `isGeolocationBlockedByPage`.
 */
export type AccessPositionResult =
  | { ok: true; coords: { latitude: number; longitude: number; accuracy?: number } }
  | { ok: false; reason: 'denied' | 'unavailable' | 'unsupported' | 'blocked' };

/**
 * Is geolocation refused by the EMBEDDING PAGE rather than by the visitor?
 *
 * ## Why this is a distinct reason, and not just `denied`
 *
 * This app mounts into a shadow root on the brand's own page (`index.tsx` —
 * `host.attachShadow`), NOT into an iframe, so there is no `allow="geolocation"`
 * attribute to get wrong and no per-frame permissions policy to inherit. That was
 * checked before writing this, because if it WERE an iframe the fix would be a one-line
 * attribute on the embed rather than anything in here.
 *
 * Two things at the DOCUMENT level can still refuse it, and neither is fixable from the
 * visitor's browser settings:
 *
 *  - **The page is not a secure context.** A brand serving the storefront over plain
 *    `http:` gets `getCurrentPosition` refused outright. `window.isSecureContext` is
 *    universally supported and definitive.
 *  - **The brand's own response carries `Permissions-Policy: geolocation=()`.** This is
 *    a top-level document policy, so it also applies to us. `document.featurePolicy` is
 *    Chromium-only and non-standard — it is read behind optional chaining and treated as
 *    "no opinion" everywhere else, so this never turns into a false positive on Safari,
 *    which is the browser most of this app's traffic uses.
 *
 * A document-policy refusal ALSO reports `denied` from `permissions.query`, which is
 * exactly why this has to be checked FIRST: told `denied`, we would send someone to a
 * settings screen where the location toggle either is not there or does nothing, with no
 * way for them to discover that. The `blocked` copy names the page as the cause instead.
 */
interface DocumentFeaturePolicy {
  allowsFeature?: (feature: string) => boolean;
}

export const isGeolocationBlockedByPage = (): boolean => {
  try {
    if (typeof window !== 'undefined' && window.isSecureContext === false) return true;

    const policy = (
      document as Document & {
        featurePolicy?: DocumentFeaturePolicy;
        permissionsPolicy?: DocumentFeaturePolicy;
      }
    );
    const allows = policy.featurePolicy?.allowsFeature ?? policy.permissionsPolicy?.allowsFeature;
    if (allows && allows.call(policy.featurePolicy ?? policy.permissionsPolicy, 'geolocation') === false) {
      return true;
    }
  } catch {
    /* Non-standard API, or a cross-origin quirk. No opinion is the safe answer. */
  }
  return false;
};

/**
 * Watch the browser's geolocation permission and report every change until cancelled.
 *
 * ## The dead end this closes
 *
 * When the stored permission is `denied`, `requestPositionForAccess` short-circuits on
 * the pre-flight and never calls `getCurrentPosition`. So a retry writes back the state
 * it already held, React renders identical output, and NOTHING a visitor can do on the
 * page changes the answer — the only thing that can is in browser settings, off the
 * page. Left there, the gate is unpassable and looks broken while being unpassable.
 *
 * `PermissionStatus` is an `EventTarget` with a `change` event (Chrome, Safari 16+,
 * Firefox), so the page can SEE the visitor flip the setting and recover on its own
 * instead of requiring them to guess that they must come back and tap again.
 *
 * Returns its own cleanup. The `cancelled` flag matters: `permissions.query` is async,
 * so an unmount can land before the listener is attached, and without the flag we would
 * attach to a status object nobody ever detaches from.
 *
 * Silently does nothing where `navigator.permissions` is absent (Safari < 16). That is
 * not a regression — those browsers keep the manual retry, which is the same thing they
 * had before.
 */
export const watchGeolocationPermission = (
  onChange: (state: GeolocationPermissionState) => void,
): (() => void) => {
  let cancelled = false;
  let detach: (() => void) | null = null;

  void (async () => {
    try {
      if (!navigator?.permissions?.query) return;
      const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
      if (cancelled) return;

      const handler = () => onChange((status.state as GeolocationPermissionState) || 'unknown');
      status.addEventListener('change', handler);
      detach = () => status.removeEventListener('change', handler);
    } catch {
      /* No observer here. The manual retry is the fallback and is always present. */
    }
  })();

  return () => {
    cancelled = true;
    detach?.();
    detach = null;
  };
};

/**
 * 15s, raised from the 6s race cap this replaces.
 *
 * The 6s was budgeted for the MEDIAN, and correctly so at the time: location was
 * best-effort, allow / deny / timeout all fell through to the POST with the email intact,
 * and a miss cost only a missing lat/lng on one analytics row. A timeout now costs the
 * visitor the certificate, so the budget has to cover the SLOW case instead.
 *
 * The slow case is also the modal one. With `enableHighAccuracy: false` the fix comes
 * from wifi/cell, i.e. a network round-trip to the platform's location service, and a
 * phone that has just tapped an NFC tag is typically indoors on cellular. That is the
 * same reasoning, and the same number, the pre-rewrite page used for exactly this
 * scenario.
 *
 * The RACE is kept, only widened. iOS Safari/Chrome can leave `getCurrentPosition`
 * pending forever and honour neither callback — its own `timeout` option is unreliable
 * there — so without an external cap the submit handler stalls with `isSubmitting` stuck
 * true and no error to retry from. That is the one shape a hard gate must never produce.
 */
export const ACCESS_POSITION_TIMEOUT_MS = 15000;

/**
 * A fix from the last 5 minutes is accepted rather than re-acquired, so someone tapping a
 * second tag in the same shop skips the wait entirely. Raised from 60s along with the
 * timeout: the more visitors answered from cache, the fewer reach the slow path at all.
 */
const ACCESS_POSITION_MAX_AGE_MS = 300000;

/**
 * Acquire a position where one is REQUIRED to proceed.
 *
 * MUST be called from inside a user gesture. Browsers (Safari especially) silently ignore
 * a gesture-less `getCurrentPosition` — no prompt appears and no callback fires — so an
 * on-mount prefetch would look exactly like a timeout.
 */
export const requestPositionForAccess = async (): Promise<AccessPositionResult> => {
  if (!navigator.geolocation) return { ok: false, reason: 'unsupported' };

  // BEFORE the permission pre-flight, because a page-level refusal also reads back as
  // `denied` and would otherwise be answered with settings instructions the visitor
  // cannot act on. See `isGeolocationBlockedByPage`.
  if (isGeolocationBlockedByPage()) return { ok: false, reason: 'blocked' };

  // Pre-flight. A `denied` store answers the question without a request, and answers it
  // with certainty rather than by reading an error code back.
  //
  // ⚠️ This is the branch that made "Try again" a dead end: it returns WITHOUT calling
  // `getCurrentPosition`, so a retry can only ever re-derive the same answer. The retry
  // is still worth keeping live (a visitor who has just fixed the setting gets through
  // on this press), but it cannot be the ONLY route out — `watchGeolocationPermission`
  // is what makes the state recoverable without a tap.
  if ((await getGeolocationPermissionState()) === 'denied') {
    return { ok: false, reason: 'denied' };
  }

  return new Promise<AccessPositionResult>((resolve) => {
    let settled = false;
    const finish = (result: AccessPositionResult) => {
      if (settled) return;
      settled = true;
      clearTimeout(cap);
      resolve(result);
    };

    // The external cap. See ACCESS_POSITION_TIMEOUT_MS — this, not the `timeout` option,
    // is what guarantees this promise settles on iOS.
    const cap = setTimeout(
      () => finish({ ok: false, reason: 'unavailable' }),
      ACCESS_POSITION_TIMEOUT_MS,
    );

    navigator.geolocation.getCurrentPosition(
      ({ coords }) =>
        finish({
          ok: true,
          coords: {
            latitude: coords.latitude,
            longitude: coords.longitude,
            accuracy: coords.accuracy ?? undefined,
          },
        }),
      (error) =>
        finish({
          ok: false,
          // PERMISSION_DENIED is reachable even though the pre-flight rejected a stored
          // `denied`: when the store said `prompt`, THIS is the prompt, and the visitor
          // can decline it. TIMEOUT and POSITION_UNAVAILABLE are the only other codes and
          // both mean "permission is fine, no fix arrived" — which is the case that must
          // NOT be told to go and change a browser setting that is already correct.
          reason: error.code === error.PERMISSION_DENIED ? 'denied' : 'unavailable',
        }),
      {
        // City-level accuracy is all the geocoder needs, and low accuracy resolves from
        // wifi/cell rather than waiting on a GPS lock.
        enableHighAccuracy: false,
        timeout: ACCESS_POSITION_TIMEOUT_MS,
        maximumAge: ACCESS_POSITION_MAX_AGE_MS,
      },
    );
  });
};

/**
 * Does the cached consent still satisfy the gate AS IT IS CONFIGURED NOW?
 *
 * Returns the consent when it does, `null` when the visitor must go through the gate
 * again. Called by the gate route and by the certificate page with the same argument, so
 * they always agree — if they could disagree, one would redirect to the gate and the
 * other would bounce straight back, with no press of Back that escaped it.
 *
 * ## The requirement is a PAIR, because the two settings are independent
 *
 * `collect_email` and `collect_location` each gate the details on their own, so all four
 * combinations are configurable and a cached consent has to be checked against BOTH
 * halves of whichever one is live now. Taking one object rather than two positional
 * booleans is not tidiness: the two call sites must pass the same argument or they
 * ping-pong, and two same-typed positionals are exactly the shape that lets one of them
 * be transposed without a type error.
 *
 * A consent written on the location-only gate has NO email, and that must fail an email
 * requirement rather than pass it by absence — a brand switching `collect_email` back on
 * would otherwise wave every such visitor through for the rest of the 180-day TTL.
 *
 * Two ways a stored consent stops covering a required location, and they are different:
 *
 *  - `locationConsent: false` — cached when the brand collected no location, or before
 *    the brand switched it on. It never asserted a location at all.
 *  - `locationConsent: true`, but the BROWSER grant is gone. The visitor withdrew it
 *    after the fact, which is also the shape of a deliberate bypass: satisfy the gate
 *    once, revoke the permission, then read for the rest of the 180-day TTL with nothing
 *    ever recorded.
 *
 * A granted permission is accepted WITHOUT re-acquiring a position here. That is the
 * deliberate line between the gate and the analytics: the requirement is that this
 * visitor shares their location, and a granted per-origin permission IS that. Demanding
 * a fresh fix would lock a returning visitor out over a transient indoor GPS miss, which
 * is the exact "strand a customer holding a real product" failure the gate must not
 * introduce. The position is still re-acquired for the LOG (never replayed from storage);
 * a miss there costs one row's lat/lng, not the page.
 */
export interface AccessRequirement {
  /** `collect_email`, resolved for this certificate. */
  email: boolean;
  /** `collect_location`, resolved for this certificate. */
  location: boolean;
}

export const cachedAccessSatisfies = async (
  requirement: AccessRequirement,
): Promise<AccessConsent | null> => {
  const consent = readAccessConsent();
  if (!consent) return null;
  if (requirement.email && !consent.email) return null;
  if (!requirement.location) return consent;
  if (!consent.locationConsent) return null;

  // `unknown` (no `navigator.permissions`) is accepted: we cannot see a revocation on
  // those browsers, and re-gating everyone we cannot see would be a permanent re-ask on
  // Safari < 16 rather than a fix.
  const state = await getGeolocationPermissionState();
  return state === 'granted' || state === 'unknown' ? consent : null;
};

/**
 * Re-acquire position for a cached-consent access, WITHOUT ever showing a prompt.
 *
 * Gated on `permissions.query` returning `granted`, so a visitor who consented in our UI
 * but whose browser permission has since been revoked (or was never granted, since our
 * checkbox and the browser grant are two different things) is simply logged without a
 * location rather than being interrupted by a prompt they did not initiate. Resolves null
 * on anything unexpected — location is best-effort here exactly as it is in the gate.
 */
export const getLocationIfAlreadyPermitted = async (): Promise<
  { latitude: number; longitude: number } | null
> => {
  try {
    if (!navigator.geolocation || !navigator.permissions) return null;
    const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
    if (status.state !== 'granted') return null;

    return await new Promise((resolve) => {
      // Same hard cap as the gate: iOS can leave getCurrentPosition pending indefinitely
      // and its own `timeout` option is unreliable there.
      const cap = setTimeout(() => resolve(null), 6000);
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          clearTimeout(cap);
          resolve({ latitude: coords.latitude, longitude: coords.longitude });
        },
        () => {
          clearTimeout(cap);
          resolve(null);
        },
        { enableHighAccuracy: false, timeout: 6000 },
      );
    });
  } catch {
    return null;
  }
};
